export const prizeTypeOptions = [
  { value: 'trophies', label: 'Pokaalit' },
  { value: 'gift_card', label: 'Lahjakortti' },
  { value: 'product_prizes', label: 'Tuotepalkinnot' },
  { value: 'cash', label: 'Käteinen' },
];

export const payoutPrizeTypes = ['gift_card', 'cash'];

export const isPayoutPrizeType = (prizeType) => payoutPrizeTypes.includes(prizeType || 'cash');

export const payoutPercentageOptions = Array.from({ length: 21 }, (_, index) => index * 5);

export const createDefaultPrizeSettings = () => ({
  payoutAmount: '',
  prizeType: 'cash',
  rules: [],
});

export const createDefaultPrizeRule = () => ({
  id: `rule-${Date.now()}-${Math.round(Math.random() * 100000)}`,
  minPlayers: '8',
  winnerCount: 4,
  percentages: ['', '', ''],
});

export const getMinPlayerOptions = (currentValue = null) => {
  const options = [1];
  let current = 2;

  while (current <= 256) {
    options.push(current);
    current *= 2;
  }

  const numericCurrentValue = Number(currentValue || 0);

  if (numericCurrentValue > 0 && !options.includes(numericCurrentValue)) {
    options.push(numericCurrentValue);
    options.sort((left, right) => left - right);
  }

  return options;
};

export const parseMoneyValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const normalized = String(value).replace(',', '.').trim();
  const parsed = Number(normalized);

  if (Number.isNaN(parsed)) {
    return null;
  }

  return Math.round(parsed * 100) / 100;
};

export const parsePercentageValue = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }

  const normalized = String(value).trim();

  if (!/^\d+$/.test(normalized)) {
    return null;
  }

  return Number(normalized);
};

export const formatMoney = (value) => {
  const parsed = parseMoneyValue(value);

  if (parsed === null) {
    return '0,00 EUR';
  }

  return `${parsed.toLocaleString('fi-FI', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })} EUR`;
};

export const getPrizeDistributionBuckets = (winnerCount) => {
  const numericWinnerCount = Number(winnerCount || 0);

  if (!numericWinnerCount || numericWinnerCount < 1) {
    return [];
  }

  const buckets = [
    {
      key: 'winner',
      label: 'Voittaja',
      placementLabel: '1.',
      playerCount: 1,
    },
  ];

  if (numericWinnerCount >= 2) {
    buckets.push({
      key: 'second',
      label: 'Toinen',
      placementLabel: '2.',
      playerCount: 1,
    });
  }

  let startPlacement = 3;
  let groupSize = 2;

  while (startPlacement <= numericWinnerCount) {
    const endPlacement = Math.min(numericWinnerCount, startPlacement + groupSize - 1);
    buckets.push({
      key: `range_${startPlacement}_${endPlacement}`,
      label: `Sijat ${startPlacement}-${endPlacement}`,
      placementLabel: `${startPlacement}.-${endPlacement}.`,
      playerCount: endPlacement - startPlacement + 1,
    });
    startPlacement = endPlacement + 1;
    groupSize *= 2;
  }

  return buckets;
};

export const getWinnerCountOptions = (minPlayers) => {
  const numericMinPlayers = Number(minPlayers || 0);

  if (numericMinPlayers === 1) {
    return [1];
  }

  const options = [];
  let current = 1;

  while (current > 0 && current < numericMinPlayers) {
    options.push(current);
    current *= 2;
  }

  return options;
};

export const getRulePercentageTotal = (rule) => (
  Array.isArray(rule?.percentages)
    ? rule.percentages.reduce((sum, percentage) => sum + (parsePercentageValue(percentage) ?? 0), 0)
    : 0
);

export const calculatePayoutPool = (totalEntryFees, payoutPercentage) => {
  const parsedEntryFees = parseMoneyValue(totalEntryFees) ?? 0;
  const parsedPayoutPercentage = parsePercentageValue(payoutPercentage) ?? 0;
  return Math.round(parsedEntryFees * (parsedPayoutPercentage / 100) * 100) / 100;
};

export const calculatePrizePreview = (rule, payoutPercentage, totalEntryFees) => {
  const totalPayout = calculatePayoutPool(totalEntryFees, payoutPercentage);
  const buckets = getPrizeDistributionBuckets(rule?.winnerCount);

  return buckets.map((bucket, index) => {
    const percentage = parsePercentageValue(rule?.percentages?.[index]) ?? 0;
    const totalAmount = Math.round(totalPayout * (percentage / 100) * 100) / 100;
    const perPlayerAmount = bucket.playerCount > 0
      ? Math.round((totalAmount / bucket.playerCount) * 100) / 100
      : 0;

    return {
      ...bucket,
      percentage,
      totalAmount,
      perPlayerAmount,
    };
  });
};

export const normalizePrizeRuleForUi = (rule, index = 0) => {
  const minPlayers = String(rule?.minPlayers ?? '8');
  const winnerCountOptions = getWinnerCountOptions(minPlayers);
  const fallbackWinnerCount = winnerCountOptions.includes(Number(rule?.winnerCount))
    ? Number(rule?.winnerCount)
    : (winnerCountOptions[winnerCountOptions.length - 1] ?? 1);
  const buckets = getPrizeDistributionBuckets(fallbackWinnerCount);

  return {
    id: rule?.id || `rule-${Date.now()}-${index}`,
    minPlayers,
    winnerCount: fallbackWinnerCount,
    percentages: buckets.map((bucket, bucketIndex) => {
      const source = Array.isArray(rule?.percentages) ? rule.percentages[bucketIndex] : '';
      return source === null || source === undefined ? '' : String(source);
    }),
  };
};

export const normalizePrizeSettingsForUi = (prizeSettings) => ({
  payoutAmount: prizeSettings?.payoutAmount === null || prizeSettings?.payoutAmount === undefined
    ? ''
    : String(prizeSettings.payoutAmount),
  prizeType: prizeSettings?.prizeType || 'cash',
  rules: Array.isArray(prizeSettings?.rules)
    ? prizeSettings.rules.map((rule, index) => normalizePrizeRuleForUi(rule, index))
    : [],
});

export const buildPrizeSettingsPayload = (prizeSettings) => ({
  prizeType: prizeSettings?.prizeType || 'cash',
  payoutAmount: isPayoutPrizeType(prizeSettings?.prizeType)
    ? parsePercentageValue(prizeSettings?.payoutAmount)
    : null,
  rules: isPayoutPrizeType(prizeSettings?.prizeType) && Array.isArray(prizeSettings?.rules)
    ? prizeSettings.rules.map((rule) => ({
        minPlayers: Number(rule.minPlayers || 0),
        winnerCount: Number(rule.winnerCount || 1),
        percentages: getPrizeDistributionBuckets(rule.winnerCount).map((bucket, index) => (
          parsePercentageValue(rule.percentages?.[index]) ?? 0
        )),
      }))
    : [],
});

export const validatePrizeSettings = (prizeSettings) => {
  if (!isPayoutPrizeType(prizeSettings?.prizeType)) {
    return null;
  }

  const rules = Array.isArray(prizeSettings?.rules) ? prizeSettings.rules : [];
  const minimums = new Set();
  const payoutPercentage = parsePercentageValue(prizeSettings?.payoutAmount);

  if (payoutPercentage !== null && (payoutPercentage < 0 || payoutPercentage > 100)) {
    return 'Payout-prosentin tulee olla välillä 0-100.';
  }

  if (prizeSettings?.payoutAmount !== '' && payoutPercentage === null) {
    return 'Payout-prosentin tulee olla kokonaisluku välillä 0-100.';
  }

  for (const rule of rules) {
    const minPlayers = Number(rule.minPlayers || 0);
    const winnerCount = Number(rule.winnerCount || 0);

    if (minPlayers < 1) {
      return 'Palkintorivin minimipelaajamäärän tulee olla vähintään 1.';
    }

    if (minimums.has(minPlayers)) {
      return 'Sama minimipelaajamäärä voi olla palkintoasetuksissa vain kerran.';
    }

    minimums.add(minPlayers);

    if (!winnerCount || (minPlayers === 1 ? winnerCount !== 1 : winnerCount >= minPlayers)) {
      return minPlayers === 1
        ? 'Kun minimipelaajamäärä on 1, palkittavia pelaajia tulee olla 1.'
        : 'Palkittavien pelaajien määrän tulee olla pienempi kuin minimipelaajamäärä.';
    }

    const buckets = getPrizeDistributionBuckets(winnerCount);
    let total = 0;

    for (let index = 0; index < buckets.length; index += 1) {
      const percentage = parsePercentageValue(rule.percentages?.[index]);

      if (percentage === null || percentage < 0) {
        return 'Aseta kaikille palkintoryhmille kokonainen prosenttiosuus.';
      }

      total += percentage;
    }

    if (total !== 100) {
      return 'Palkintorivin prosenttien summan tulee olla tasan 100 %.';
    }
  }

  return null;
};